import { useState } from "react";

import { Button } from "@fluentui/react-components";
import { useAtomValue } from "jotai";
import { devices } from "library/constants";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { selectedClientAtom } from "store";
import styled from "styled-components";

import { AppLogoSpinner } from "components/AppLogoSpinner/AppLogoSpinner";
import { EmployeeDetailsList } from "components/EmployeeDetailsList/EmployeeDetailsList";
import { UploadIcon } from "components/UploadIcon/UploadIcon";
import { useAppTheme } from "hooks/useAppTheme";
import useEmployee from "hooks/useEmployee";
import { useHasGivenConsent } from "hooks/useHasGivenConsent";
import { useUpdateEmployee } from "state/useUpdateEmployee";

import { AvatarPickerDialog } from "./AvatarPickerDialog";

export function ProfileHeader() {
  const { t } = useTranslation();
  const selectedClient = useAtomValue(selectedClientAtom);
  const { employeeData } = useEmployee();
  const theme = useAppTheme();
  const hasGivenConsent = useHasGivenConsent();
  const [pickerOpen, setPickerOpen] = useState(false);

  const { mutate: onUpdateEmployee, isLoading: updateEmployeeInProgress } =
    useUpdateEmployee(false);

  const handleAvatarSelect = (formattedFile: {
    base64String: string;
    name: string;
    type: string;
    path: string;
  }) => {
    onUpdateEmployee(
      {
        countryCode: Number(selectedClient?.countryCode),
        clientInternalNumberGOS: Number(selectedClient?.clientInternalNumberGos),
        payload: {
          ...employeeData,
          backgroundPictureContent: formattedFile.base64String,
          backgroundPictureName: formattedFile.name,
          backgroundPictureMimeType: formattedFile.type,
          backgroundPicturePath: formattedFile.path,
        },
      },
      {
        onSuccess() {
          toast.success(t("hbh.employeeUpdatedSuccessfully.label"));
        },
      }
    );
  };

  return (
    <Container>
      {updateEmployeeInProgress && <AppLogoSpinner />}

      <EmployeeDetailsWrap>
        {employeeData?.backgroundPicturePath ? (
          <img src={employeeData?.backgroundPicturePath} alt="user-profile" />
        ) : (
          <NoImagePlaceholder>{`${employeeData?.firstName?.charAt(0)}${employeeData?.lastName?.charAt(0)}`}</NoImagePlaceholder>
        )}
        <Button
          data-help="profile-avatar-button"
          disabled={!hasGivenConsent}
          style={{
            position: "absolute",
            left: "80px",
            bottom: "10px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)",
          }}
          onClick={() => setPickerOpen(true)}
          shape="circular"
          appearance="primary"
          icon={<UploadIcon fill={theme.palette.white} />}
        />
        <EmployeeDetailsList showName />
      </EmployeeDetailsWrap>

      <AvatarPickerDialog
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleAvatarSelect}
      />
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  gap: 5px;
  justify-content: flex-end;
  position: relative;
  width: 100%;

  @media only screen and ${devices.md} {
  }
`;

const EmployeeDetailsWrap = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  margin-top: 20px;
  img {
    width: 110px;
    height: 110px;
    border: 2px solid ${({ theme }) => theme.palette.white};
    background: ${({ theme }) => theme.palette.neutralLighterAlt};
    border-radius: 50%;
  }
  @media only screen and ${devices.md} {
  }
`;

const NoImagePlaceholder = styled.div`
  display: flex;
  align-items: center;
  font-size: 20px;
  justify-content: center;
  font-weight: 600;

  width: 110px;
  height: 110px;
  border: 2px solid ${({ theme }) => theme.palette.white};
  background: ${({ theme }) => theme.palette.neutralLighterAlt};
  border-radius: 50%;
`;
